import type { HelperMessage } from "../game/help/FakeFifo";

export type Identity = { displayName: string; tag: string };

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

export class UiController {
  private root: HTMLElement;
  private hud: HTMLElement;
  private msgCard: HTMLElement;
  private msgFrom: HTMLElement;
  private msgBody: HTMLElement;
  private cinema: HTMLElement;
  private cinemaTitle: HTMLElement;
  private cinemaStack: HTMLElement;
  private screens: Record<string, HTMLElement> = {};

  onStart?: (id: Identity) => void;
  onAcceptHelp?: () => void;
  onRefuseHelp?: () => void;
  onCompose?: (body: string) => void;
  onShowBoard?: () => void;
  onBackTitle?: () => void;
  onRetry?: () => void;

  constructor(root: HTMLElement) {
    this.root = root;
    this.hud = el("div", "hud");
    this.hud.innerHTML = `<span class="spares">One hit</span><span class="act">Alone</span>`;
    this.hud.classList.add("hidden");
    this.hud.style.display = "none";

    this.msgCard = el("div", "message-card");
    this.msgFrom = el("div", "from");
    this.msgBody = el("div", "body");
    this.msgCard.append(this.msgFrom, this.msgBody);

    this.cinema = el("div", "rescue-cinema");
    this.cinemaTitle = el("div", "rc-title", "Rescue signal");
    this.cinemaStack = el("div", "rc-stack");
    this.cinema.append(this.cinemaTitle, this.cinemaStack);

    this.root.append(this.hud, this.msgCard, this.cinema);
    this.buildTitle();
    this.buildHelp();
    this.buildResults();
    this.buildCompose();
    this.buildBoard();
    this.bindMobileKeyboard();
    this.show("title");
  }

  /**
   * Ending E–weight Accept beat: freeze feels like world holds,
   * dim, staggered name + quote fades, then resolve.
   */
  playRescueCinema(messages: HelperMessage[]): Promise<void> {
    return new Promise((resolve) => {
      this.cinemaStack.innerHTML = "";
      this.cinemaTitle.classList.remove("show");
      this.cinema.classList.add("active");
      // Force reflow so transitions run
      void this.cinema.offsetWidth;
      this.cinema.style.background = "rgba(0,0,0,0.72)";
      window.setTimeout(() => this.cinemaTitle.classList.add("show"), 120);

      const lines = messages.length > 0 ? messages : [
        {
          id: "fallback",
          displayName: "Archive",
          body: "We left these words in the dark so you would not be alone here.",
        },
      ];

      let delay = 500;
      lines.forEach((msg, i) => {
        const row = el("div", "rc-line");
        const name = el(
          "div",
          "rc-name",
          msg.tag ? `${msg.displayName} · ${msg.tag}` : msg.displayName,
        );
        const body = el("div", "rc-body", msg.body);
        row.append(name, body);
        this.cinemaStack.append(row);
        window.setTimeout(() => row.classList.add("show"), delay);
        delay += 700;
      });

      // Hold last line, then fade out
      const total = delay + 1100;
      window.setTimeout(() => {
        this.cinema.style.background = "rgba(0,0,0,0)";
        this.cinemaTitle.classList.remove("show");
        this.cinemaStack.querySelectorAll(".rc-line").forEach((n) => {
          n.classList.remove("show");
        });
      }, total - 500);
      window.setTimeout(() => {
        this.cinema.classList.remove("active");
        this.cinemaStack.innerHTML = "";
        resolve();
      }, total);
    });
  }

  /** Keep focused fields above the soft keyboard on phones. */
  private bindMobileKeyboard(): void {
    const shell = document.getElementById("shell");
    const applyVv = () => {
      const vv = window.visualViewport;
      if (!vv || !shell) return;
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      document.documentElement.style.setProperty("--kb-inset", `${inset}px`);
      document.documentElement.style.setProperty("--vv-height", `${vv.height}px`);
      shell.classList.toggle("kb-open", inset > 80);
    };
    window.visualViewport?.addEventListener("resize", applyVv);
    window.visualViewport?.addEventListener("scroll", applyVv);
    window.addEventListener("resize", applyVv);
    applyVv();

    const scrollField = (target: EventTarget | null) => {
      const node = target as HTMLElement | null;
      if (!node || !(node instanceof HTMLElement)) return;
      if (node.tagName !== "INPUT" && node.tagName !== "TEXTAREA") return;
      window.setTimeout(() => {
        node.scrollIntoView({ block: "center", behavior: "smooth" });
        applyVv();
      }, 120);
    };
    this.root.addEventListener("focusin", (e) => scrollField(e.target));
  }

  private wireField(input: HTMLInputElement | HTMLTextAreaElement): void {
    input.autocapitalize = input instanceof HTMLTextAreaElement ? "sentences" : "words";
    input.setAttribute("autocorrect", "on");
    input.setAttribute("spellcheck", "true");
    // Stop canvas / body touch-action from eating text gestures
    input.style.touchAction = "manipulation";
  }

  private buildTitle(): void {
    const s = el("div", "screen form-screen");
    s.id = "screen-title";
    const block = el("div", "form-block");
    block.append(
      el("h1", undefined, "Ending E"),
      el(
        "p",
        "subtitle",
        "Ending E–style credit flight. Drag anywhere to move (Bullet Hell Monday style). Auto-fire. One hit ends the flight — accept help or start over. Not affiliated with Square Enix.",
      ),
    );
    const nameLabel = el("label", undefined, "Display name");
    nameLabel.htmlFor = "name-input";
    const name = el("input") as HTMLInputElement;
    name.id = "name-input";
    name.name = "displayName";
    name.type = "text";
    name.maxLength = 24;
    name.placeholder = "Visitor";
    name.enterKeyHint = "next";
    name.inputMode = "text";
    name.autocomplete = "username";
    name.setAttribute("autocomplete", "username");
    this.wireField(name);

    const tagLabel = el("label", undefined, "Tag (optional)");
    tagLabel.htmlFor = "tag-input";
    const tag = el("input") as HTMLInputElement;
    tag.id = "tag-input";
    tag.name = "tag";
    tag.type = "text";
    tag.maxLength = 8;
    tag.placeholder = "00";
    tag.enterKeyHint = "go";
    tag.inputMode = "text";
    tag.autocomplete = "off";
    this.wireField(tag);

    name.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        tag.focus();
      }
    });
    tag.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        start.click();
      }
    });

    const start = el("button", "btn primary", "Begin") as HTMLButtonElement;
    start.type = "button";
    start.addEventListener("click", () => {
      name.blur();
      tag.blur();
      const displayName = name.value.trim() || "Visitor";
      const t = tag.value.trim();
      this.onStart?.({ displayName, tag: t });
    });

    const board = el("button", "btn", "Those who made it") as HTMLButtonElement;
    board.type = "button";
    board.addEventListener("click", () => this.onShowBoard?.());

    const row = el("div", "btn-row");
    row.append(start, board);
    block.append(nameLabel, name, tagLabel, tag, row);
    s.append(block);
    s.append(el("div", "fan-note", "Fan tribute · Open-license audio · Cloudflare"));
    this.root.append(s);
    this.screens.title = s;
  }

  private buildHelp(): void {
    const s = el("div", "screen hidden");
    s.id = "screen-help";
    // After 4 falls (wiki-faithful). Original wording — not SE copy.
    s.append(
      el("h1", undefined, "Rescue"),
      el(
        "p",
        "subtitle",
        "A signal answers. Strangers who already cleared this sky offer their ships. Accept, and they stand with you. Refuse, and the ritual begins again from silence.",
      ),
    );
    const note = el(
      "p",
      "subtitle",
      "While they fly with you, a hit may claim one of their ships instead of ending yours — for a moment.",
    );
    note.style.fontSize = "0.75rem";
    note.style.opacity = "0.85";
    s.append(note);
    const accept = el(
      "button",
      "btn primary",
      "Accept rescue",
    ) as HTMLButtonElement;
    const refuse = el(
      "button",
      "btn",
      "Refuse — start over",
    ) as HTMLButtonElement;
    accept.type = "button";
    refuse.type = "button";
    accept.addEventListener("click", () => this.onAcceptHelp?.());
    refuse.addEventListener("click", () => this.onRefuseHelp?.());
    const row = el("div", "btn-row");
    row.append(accept, refuse);
    s.append(row);
    this.root.append(s);
    this.screens.help = s;
  }

  private buildResults(): void {
    const s = el("div", "screen hidden");
    s.id = "screen-results";
    s.append(el("h1", undefined, "Clear"), el("p", "subtitle", ""));
    const sub = s.querySelector(".subtitle") as HTMLElement;
    sub.id = "results-sub";

    const compose = el("button", "btn primary", "Leave a message") as HTMLButtonElement;
    const board = el("button", "btn", "Those who made it") as HTMLButtonElement;
    const again = el("button", "btn", "Fly again") as HTMLButtonElement;
    compose.type = "button";
    board.type = "button";
    again.type = "button";
    compose.id = "btn-compose";
    compose.addEventListener("click", () => this.show("compose"));
    board.addEventListener("click", () => this.onShowBoard?.());
    again.addEventListener("click", () => this.onRetry?.());
    const row = el("div", "btn-row");
    row.append(compose, board, again);
    s.append(row);
    this.root.append(s);
    this.screens.results = s;
  }

  private buildCompose(): void {
    const s = el("div", "screen form-screen hidden");
    s.id = "screen-compose";
    const block = el("div", "form-block");
    block.append(
      el("h1", undefined, "Message"),
      el(
        "p",
        "subtitle",
        "100–140 characters. No links. Your words enter the global help queue for everyone.",
      ),
    );
    const ta = el("textarea") as HTMLTextAreaElement;
    ta.id = "compose-body";
    ta.name = "message";
    ta.maxLength = 140;
    ta.rows = 5;
    ta.placeholder = "Words for the next pilot…";
    ta.enterKeyHint = "send";
    ta.inputMode = "text";
    this.wireField(ta);
    const count = el("div", "char-count", "0 / 140");
    count.id = "compose-count";
    ta.addEventListener("input", () => {
      const n = ta.value.length;
      count.textContent = `${n} / 140`;
      count.classList.toggle("bad", n > 0 && (n < 100 || n > 140));
    });
    const submit = el(
      "button",
      "btn primary",
      "Submit to the queue",
    ) as HTMLButtonElement;
    const back = el("button", "btn", "Back") as HTMLButtonElement;
    submit.type = "button";
    back.type = "button";
    submit.addEventListener("click", () => {
      const body = ta.value.trim();
      if (body.length < 100 || body.length > 140) return;
      if (/https?:\/\//i.test(body) || /www\./i.test(body)) return;
      ta.blur();
      this.onCompose?.(body);
    });
    back.addEventListener("click", () => {
      ta.blur();
      this.show("results");
    });
    const row = el("div", "btn-row");
    row.append(submit, back);
    block.append(ta, count, row);
    s.append(block);
    this.root.append(s);
    this.screens.compose = s;
  }

  private buildBoard(): void {
    const s = el("div", "screen hidden");
    s.id = "screen-board";
    s.append(el("h1", undefined, "Those who made it"), el("p", "subtitle", "Messages from clearers. Browsing does not consume the help queue."));
    const list = el("ul", "scoreboard-list");
    list.id = "board-list";
    const back = el("button", "btn", "Back") as HTMLButtonElement;
    back.type = "button";
    back.addEventListener("click", () => this.onBackTitle?.());
    s.append(list, el("div", "btn-row"));
    (s.querySelector(".btn-row") as HTMLElement).append(back);
    this.root.append(s);
    this.screens.board = s;
  }

  show(name: keyof UiController["screens"] | "title" | "help" | "results" | "compose" | "board" | "flight"): void {
    for (const s of Object.values(this.screens)) {
      s.classList.add("hidden");
      s.style.display = "none";
    }
    if (name === "flight") {
      this.hud.style.display = "flex";
      // Dismiss keyboard when entering combat
      (document.activeElement as HTMLElement | null)?.blur?.();
      // Ensure stage is on top of empty UI layer for taps + visibility
      const stage = document.getElementById("stage");
      if (stage) stage.style.zIndex = "1";
      this.root.style.pointerEvents = "none";
      this.hud.style.pointerEvents = "none";
      return;
    }
    this.root.style.pointerEvents = "";
    this.hud.style.display = "none";
    const s = this.screens[name];
    if (s) {
      s.classList.remove("hidden");
      s.style.display = "flex";
      s.scrollTop = 0;
      // Focus primary field after paint so mobile keyboard is intentional
      if (name === "compose") {
        window.setTimeout(() => {
          document.getElementById("compose-body")?.focus();
        }, 80);
      }
    }
  }

  setHud(actName: string, progress: number, deaths: number, helpers = 0): void {
    const spareEl = this.hud.querySelector(".spares");
    const actEl = this.hud.querySelector(".act");
    if (spareEl) {
      const parts: string[] = [];
      parts.push(deaths > 0 ? `Falls ${deaths}` : "One hit");
      if (helpers > 0) parts.push(`Assist ${helpers}`);
      spareEl.textContent = parts.join(" · ");
    }
    if (actEl) {
      actEl.textContent = `${actName} · ${Math.floor(progress * 100)}%`;
    }
  }

  showHelperMessage(msg: HelperMessage): void {
    this.msgFrom.textContent =
      msg.tag != null && msg.tag !== ""
        ? `${msg.displayName} · ${msg.tag}`
        : msg.displayName;
    this.msgBody.textContent = msg.body;
    this.msgCard.classList.add("visible");
    window.setTimeout(() => this.msgCard.classList.remove("visible"), 4200);
  }

  setResults(elapsed: number, helped: boolean, deaths: number): void {
    const sub = document.getElementById("results-sub");
    if (sub) {
      const m = Math.floor(elapsed / 60);
      const s = Math.floor(elapsed % 60)
        .toString()
        .padStart(2, "0");
      sub.textContent = `${m}:${s} · ${deaths} falls · ${helped ? "Cleared with help" : "Solo clear"}`;
    }
  }

  setBoard(items: HelperMessage[]): void {
    const list = document.getElementById("board-list");
    if (!list) return;
    list.innerHTML = "";
    for (const m of items) {
      const li = el("li");
      const name = el(
        "div",
        "name",
        m.tag ? `${m.displayName} · ${m.tag}` : m.displayName,
      );
      const snip = el("div", "snip", m.body);
      li.append(name, snip);
      li.addEventListener("click", () => this.showHelperMessage(m));
      list.append(li);
    }
  }

  toast(text: string): void {
    this.msgFrom.textContent = "System";
    this.msgBody.textContent = text;
    this.msgCard.classList.add("visible");
    window.setTimeout(() => this.msgCard.classList.remove("visible"), 2800);
  }
}
